// React hook to handle stop status progression sequentially
import { usePatchStopMutation, useUpdateStopByTokenMutation } from '../redux/api/trips/tripApi';
import { StopStatus, TRIP_STATUS, TripStop } from '../redux/api/trips/trips.model';
import { useAppSelector } from '../redux/hooks';
import LoadUtils from '../utils/loadUtils';

interface UseStopStatusProgression {
  progressStopStatusForStops: (
    tripId: string,
    stops: TripStop[],
    currentTripStatus: TRIP_STATUS,
    tagsToInvalidate: any[],
    token?: string
  ) => Promise<void>;
  updateStopStatus: (
    stopId: string,
    status: StopStatus,
    currentTripStatus: TRIP_STATUS,
    tagsToInvalidate: any[],
    stopUpdatedAt: string,
    tripId?: string
  ) => Promise<any>;
}

const useStopStatusProgression = (): UseStopStatusProgression => {
  const filterParams = useAppSelector((state) => state.activeTrips.filters);
  const [patchStop] = usePatchStopMutation();
  const [updateStopByToken] = useUpdateStopByTokenMutation();

  const updateStopStatus = async (
    stopId: string,
    status: StopStatus,
    currentTripStatus: TRIP_STATUS,
    tagsToInvalidate: any[],
    stopUpdatedAt: string,
    tripId?: string
  ): Promise<any> => {
    return await patchStop({
      stopId,
      body: { status, trip_id: tripId, updated_at: stopUpdatedAt },
      invalidateTags: tagsToInvalidate,
      searchTripParams: { filters: { ...filterParams } },
      prevStatus: currentTripStatus
    }).unwrap();
  };

  const updateStopStatusByToken = async (
    token: string,
    stopId: string,
    stopUpdatedAt: string,
    tripId: string,
    status: StopStatus
  ): Promise<void> => {
    if (!token) return Promise.reject(new Error('Token is required'));

    return await updateStopByToken({
      token,
      stopId,
      stopUpdatedAt,
      tripId,
      stopStatus: status
    }).unwrap();
  };

  const progressStopStatusForStops = async (
    tripId: string,
    stops: TripStop[],
    currentTripStatus: TRIP_STATUS,
    tagsToInvalidate: any[],
    token?: string
  ): Promise<void> => {
    const nextStopStatusInfo = LoadUtils.getNextStopStatus(stops);

    if (!nextStopStatusInfo) return;
    const [nextStop, nextStopStatus] = nextStopStatusInfo;

    if (!nextStop.trip_id || !nextStop.id) {
      console.error(
        `stopId, nextStop, are required to update stop status, stopId:${nextStop.id}, nextStop:${nextStop.id}`
      );
      Promise.reject(new Error('Something went wrong, please try again'));
      return;
    }

    if (!nextStop.updated_at)
      return Promise.reject(new Error('Something went wrong, please try again'));

    if (!token) {
      await updateStopStatus(
        nextStop.id,
        nextStopStatus as StopStatus,
        currentTripStatus,
        tagsToInvalidate,
        nextStop.updated_at,
        tripId
      );
    } else if (token) {
      await updateStopStatusByToken(
        token,
        nextStop.id,
        nextStop.updated_at,
        tripId,
        nextStopStatus as StopStatus
      );
    }
  };

  return { progressStopStatusForStops, updateStopStatus };
};

export default useStopStatusProgression;
